import * as THREE from 'three';
import { MODERN } from '../materials';
import { Beam, type ModelProps, Pole, Squad } from '../parts';

/** Modern-era units: each sits on y = 0, faces +z, at real proportions (UnitModel scales them). */

const { OLIVE, DARK_OLIVE, GUNMETAL, TRACK, STEEL, GLASS, WHITE } = MODERN;

const G = {
  // Gun squad
  helmet: new THREE.SphereGeometry(0.084, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2),
  helmetRim: new THREE.CylinderGeometry(0.092, 0.092, 0.012, 10),
  vest: new THREE.CylinderGeometry(0.094, 0.11, 0.12, 8),
  mgBody: new THREE.BoxGeometry(0.035, 0.04, 0.12),
  mgBarrel: new THREE.CylinderGeometry(0.008, 0.008, 0.14, 6).rotateX(Math.PI / 2),
  ammoCan: new THREE.BoxGeometry(0.05, 0.035, 0.03),
  // Tank
  hull: new THREE.BoxGeometry(0.3, 0.09, 0.5),
  glacis: new THREE.BoxGeometry(0.3, 0.02, 0.1),
  trackBelt: new THREE.BoxGeometry(0.08, 0.1, 0.54),
  roadWheel: new THREE.CylinderGeometry(0.036, 0.036, 0.085, 8).rotateZ(Math.PI / 2),
  turret: new THREE.CylinderGeometry(0.1, 0.12, 0.08, 6),
  hatch: new THREE.CylinderGeometry(0.035, 0.035, 0.02, 8),
  tankBarrel: new THREE.CylinderGeometry(0.014, 0.017, 0.42, 8).rotateX(Math.PI / 2),
  muzzleBrake: new THREE.BoxGeometry(0.04, 0.03, 0.04),
  // Missile launcher
  wheel: new THREE.CylinderGeometry(0.05, 0.05, 0.04, 10).rotateZ(Math.PI / 2),
  chassis: new THREE.BoxGeometry(0.22, 0.04, 0.5),
  cab: new THREE.BoxGeometry(0.22, 0.11, 0.13),
  windscreen: new THREE.BoxGeometry(0.18, 0.045, 0.01),
  rack: new THREE.BoxGeometry(0.2, 0.12, 0.3),
  tube: new THREE.CylinderGeometry(0.026, 0.026, 0.012, 8).rotateX(Math.PI / 2),
  missileNose: new THREE.ConeGeometry(0.018, 0.04, 8).rotateX(Math.PI / 2),
};

/** A combat helmet and a team-coloured vest (drawn in a figure's frame). */
function HelmetAndVest({ accent }: { accent: THREE.Material }) {
  return (
    <>
      <mesh geometry={G.vest} material={accent} position={[0, 0.2, 0]} />
      <mesh geometry={G.helmet} material={DARK_OLIVE} position={[0, 0.385, 0]} />
      <mesh geometry={G.helmetRim} material={DARK_OLIVE} position={[0, 0.39, 0]} />
    </>
  );
}

/** A machine gun on a tripod, pointing +z. */
function TripodGun() {
  const apex: [number, number, number] = [0, 0.1, 0];
  return (
    <>
      {[
        [0, 0, -0.08],
        [-0.07, 0, 0.05],
        [0.07, 0, 0.05],
      ].map(([x, y, z]) => (
        <Beam key={`${x}:${z}`} from={apex} to={[x, y, z]} material={GUNMETAL} thickness={0.012} />
      ))}
      <mesh geometry={G.mgBody} material={GUNMETAL} position={[0, 0.12, 0]} />
      <mesh geometry={G.mgBarrel} material={GUNMETAL} position={[0, 0.125, 0.12]} />
      <mesh geometry={G.ammoCan} material={DARK_OLIVE} position={[0.04, 0.11, 0]} />
    </>
  );
}

/**
 * Three soldiers in olive with team-coloured vests and helmets, carbines
 * across their chests, and a tripod machine gun set up in front of the squad.
 */
export function GunSquad({ accent }: ModelProps) {
  return (
    <>
      <Squad accent={OLIVE} scale={0.75} spread={0.17}>
        <HelmetAndVest accent={accent} />
        <Pole length={0.3} position={[0.02, 0.23, 0.1]} tilt={-0.8} material={GUNMETAL} />
      </Squad>
      <group position={[0, 0, 0.36]}>
        <TripodGun />
      </group>
    </>
  );
}

const TRACK_X = 0.17;
const ROAD_WHEELS = [-0.18, -0.06, 0.06, 0.18];
const TURRET: [number, number, number] = [0, 0.2, -0.04];

/**
 * A main battle tank: an olive hull on two track belts with road wheels, a
 * team-coloured six-sided turret with a hatch and a long barrel forward.
 */
export function Tank({ accent }: ModelProps) {
  return (
    <>
      {[-TRACK_X, TRACK_X].map((x) => (
        <group key={x} position={[x, 0, 0]}>
          <mesh geometry={G.trackBelt} material={TRACK} position={[0, 0.05, 0]} />
          {ROAD_WHEELS.map((z) => (
            <mesh key={z} geometry={G.roadWheel} material={GUNMETAL} position={[0, 0.04, z]} />
          ))}
        </group>
      ))}
      <mesh geometry={G.hull} material={OLIVE} position={[0, 0.115, 0]} />
      <mesh
        geometry={G.glacis}
        material={DARK_OLIVE}
        position={[0, 0.14, 0.26]}
        rotation={[0.5, 0, 0]}
      />
      <group position={TURRET}>
        <mesh geometry={G.turret} material={accent} />
        <mesh geometry={G.hatch} material={DARK_OLIVE} position={[0.04, 0.045, -0.03]} />
        <mesh geometry={G.tankBarrel} material={STEEL} position={[0, 0.005, 0.3]} />
        <mesh geometry={G.muzzleBrake} material={GUNMETAL} position={[0, 0.005, 0.52]} />
      </group>
    </>
  );
}

const TRUCK_WHEELS = [-0.18, -0.05, 0.16];
// The launch rack's tilt (its front end raised) and the tube grid on its front face.
const RACK_TILT = 0.45;
const TUBES = [-0.06, 0, 0.06].flatMap((x) => [-0.028, 0.028].map((y) => [x, y] as const));

/**
 * A six-wheeled launcher truck: an olive cab with a windscreen at the front
 * and, over the rear axles, a team-coloured launch rack tilted up with six
 * tubes showing white missile noses.
 */
export function MissileLauncher({ accent }: ModelProps) {
  return (
    <>
      {[-0.13, 0.13].flatMap((x) =>
        TRUCK_WHEELS.map((z) => (
          <mesh key={`${x}:${z}`} geometry={G.wheel} material={TRACK} position={[x, 0.05, z]} />
        ))
      )}
      <mesh geometry={G.chassis} material={DARK_OLIVE} position={[0, 0.09, 0]} />
      <group position={[0, 0.165, 0.18]}>
        <mesh geometry={G.cab} material={OLIVE} />
        <mesh geometry={G.windscreen} material={GLASS} position={[0, 0.02, 0.066]} />
      </group>
      <Beam from={[0, 0.11, -0.04]} to={[0, 0.2, -0.04]} material={GUNMETAL} thickness={0.04} />
      <group position={[0, 0.2, -0.1]} rotation={[-RACK_TILT, 0, 0]}>
        <mesh geometry={G.rack} material={accent} />
        {TUBES.map(([x, y]) => (
          <group key={`${x}:${y}`} position={[x, y, 0.15]}>
            <mesh geometry={G.tube} material={GUNMETAL} />
            <mesh geometry={G.missileNose} material={WHITE} position={[0, 0, 0.012]} />
          </group>
        ))}
      </group>
    </>
  );
}
